import { Box, Button, Heading, Highlight, Image, Text } from '@chakra-ui/react';

import React, { useEffect } from 'react';
import { useMutation } from 'react-query';
import { axiosErrorHandler } from '../../apis/axios';
import useSnackBar from '../common/V2/Snackbar';
import { startCourse } from '../../apis/course';
import { useUserProfile } from '../../hooks/useUser';
import { useNavigate } from 'react-router-dom';
import { usePublishedItems } from '../../hooks/V2/useCourse';

const CourseCard = ({
	courseId,
	courseName,
	courseDescription,
	info,
}: {
	courseId: string;
	courseName: string;
	courseDescription: string;
	info: {
		icon: string;
		text: string;
	}[];
}) => {
	const navigate = useNavigate();
	const snackbar = useSnackBar();
	const { data: publishedItems, refetch: refetchPublishedItems } = usePublishedItems(courseId);
	const { data: userProfile, refetch: refetchUserProfile } = useUserProfile();
	const [currCourseStarted, setCurrCourseStarted] = React.useState(false);
	const [roadmapPublishedItem, setRoadmapPublishedItem] = React.useState<any>();
	useEffect(() => {
		// filter first published item of type Roadmap
		if (!publishedItems) return;
		const roadmap = publishedItems.find(item => item.entityType === 'Roadmap');
		// @ts-ignore
		setRoadmapPublishedItem(roadmap);
	}, [publishedItems]);
	const { mutate: startCourseMutate, isLoading: isStartCourseLoading } = useMutation(startCourse, {
		onSuccess: data => {
			refetchUserProfile();
		},
		onError: error => {
			const e = axiosErrorHandler(error);
			if (typeof e === 'object' && 'message' in e) {
				snackbar.error(e.message);
			} else {
				snackbar.error('Something went wrong');
			}
		},
	});
	useEffect(() => {
		userProfile?.enrolments.forEach((enrolment: { courseStatus: any; course: any }) => {
			if (enrolment?.course === courseId && enrolment.courseStatus === 'Started') {
				setCurrCourseStarted(true);
			}
		});
	}, [courseId, userProfile]);

	const handleStartCourse = (courseId: string) => {
		startCourseMutate({ courseId });
	};

	return (
		<Box
			bg="#FFFFFF"
			borderRadius="16px"
			position="relative"
			p={{ base: '16px 14px', md: '24px 36px' }}
			className="overflow-hidden"
			flexBasis="100%"
			display="flex"
			flexDir="column"
			justifyContent="space-between"
			border={'1px solid #E5E5E5'}
			boxShadow="0px 1px 2px 0px #0000000F"
			maxWidth={'600px'}
			mt={{ base: '16px', md: '18px' }}
		>
			<div>
				<Box display="flex" alignItems="center" gap="14px">
					<Heading
						as="h3"
						fontSize={{
							base: '19px !important',
							md: '24px !important',
						}}
					>
						{courseName}
					</Heading>
				</Box>

				{info.length > 0 &&
					info.map((info, infoIndex) => (
						<Box gap="8px" display="flex" key={infoIndex} alignItems="center" mt={infoIndex === 0 ? '10px' : '12px'}>
							<Image src={info.icon} alt="icon" />
							<Text textStyle="body1-md" fontSize={{ base: '14px', md: '16px' }}>
								<Highlight
									query="24 Months"
									styles={{
										fontWeight: '700',
									}}
								>
									{info.text}
								</Highlight>
							</Text>
						</Box>
					))}

				<Text mt="12px" textStyle="body1" color="#544D4F">
					{courseDescription}
				</Text>
			</div>

			<Box display={'flex'} justifyContent={{ base: 'right', md: 'left' }} gap={2}>
				<Button
					mt="16px"
					bg="#4358F6"
					variant="primary"
					textStyle="btn-md"
					size={{ base: 'md', md: 'lg' }}
					onClick={() => {
						if (!currCourseStarted) {
							handleStartCourse(courseId);
						}
						navigate(`/dashboard/published-item/${roadmapPublishedItem?._id}`);
					}}
					isLoading={isStartCourseLoading}
				>
					{currCourseStarted ? 'Continue Course' : 'START COURSE'}
				</Button>
				<Button
					mt="16px"
					color={'#4358F6'}
					_hover={{ bg: '#4358f624' }}
					variant={'outline'}
					textStyle="btn-md"
					size={{ base: 'md', md: 'lg' }}
					onClick={() => {
						navigate(`/dashboard/course/${courseId}/syllabus`);
					}}
				>
					{'View Syllabus'}
				</Button>
			</Box>
		</Box>
	);
};

export default CourseCard;
